import { motion } from "framer-motion";
import { Check } from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const chartData = [
  { month: "Ene", ingresos: 6200, gastos: 2800 },
  { month: "Feb", ingresos: 5800, gastos: 3100 },
  { month: "Mar", ingresos: 7100, gastos: 2600 },
  { month: "Abr", ingresos: 6500, gastos: 2900 },
  { month: "May", ingresos: 8310, gastos: 2742 },
  { month: "Jun", ingresos: 7800, gastos: 3200 },
  { month: "Jul", ingresos: 8900, gastos: 2500 },
  { month: "Ago", ingresos: 7600, gastos: 3000 },
  { month: "Sep", ingresos: 9200, gastos: 2700 },
  { month: "Oct", ingresos: 8100, gastos: 3100 },
  { month: "Nov", ingresos: 9500, gastos: 2850 },
  { month: "Dic", ingresos: 10200, gastos: 3400 },
];

const benefits = [
  "Ves de dónde viene cada euro y a dónde va, sin necesidad de ser contador.",
  "El cierre del mes listo para presentar, sin preparar nada la noche anterior.",
  "Si el contador o el consejo pastoral lo necesita, lo tienes en segundos.",
  "El registro de cada movimiento, disponible cuando alguien hace una pregunta incómoda.",
];

const fade = {
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.75 },
};

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl bg-white p-4 shadow-lg border border-violet-light">
      <p className="font-display font-semibold text-ink mb-2">{label}</p>
      {payload.map((entry) => (
        <p key={entry.name} className="text-base text-ink-2">
          <span
            className="inline-block w-3 h-3 rounded-full mr-2"
            style={{ backgroundColor: entry.color }}
          />
          {entry.name === "ingresos" ? "Ingresos" : "Gastos"}:{" "}
          <span className="font-semibold text-ink">
            {entry.value.toLocaleString("es-ES")} EUR
          </span>
        </p>
      ))}
    </div>
  );
}

export default function FinancialsSection() {
  return (
    <section
      id="financials"
      data-testid="financials-section"
      className="py-20 md:py-32 bg-white border-t border-violet-light"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 md:px-12">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <motion.div {...fade}>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-gold mb-4">
              Finanzas
            </p>
            <h2
              data-testid="financials-title"
              className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-ink tracking-tight"
            >
              Las cuentas de la iglesia, claras para el liderazgo
            </h2>
            <p className="mt-5 text-xl text-ink-2 leading-relaxed">
              El tesorero registra, tú consultas cuando lo necesitas. Llegas a cada reunión
              con los números claros y tomas decisiones con confianza, no con suposiciones.
            </p>
            <ul className="mt-8 space-y-4">
              {benefits.map((text) => (
                <li key={text} className="flex items-start gap-3 text-lg text-ink-2">
                  <span className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-violet-light text-violet-fuerte">
                    <Check className="h-4 w-4" strokeWidth={2.5} />
                  </span>
                  {text}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            {...fade}
            className="rounded-3xl border border-violet-light bg-white p-6 md:p-8 shadow-[0_12px_48px_-16px_rgba(74,58,138,0.1)]"
            data-testid="financials-chart"
          >
            <div className="mb-6 flex flex-wrap items-end justify-between gap-3 border-b border-violet-light pb-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-violet-fuerte">
                  Ingresos vs. Gastos
                </p>
                <p className="mt-1 text-base text-ink-3">Serie anual (ejemplo)</p>
              </div>
              <div className="flex gap-4">
                <span className="flex items-center gap-2 text-sm text-ink-2">
                  <span className="h-3 w-3 rounded-full bg-violet-fuerte" />
                  Ingresos
                </span>
                <span className="flex items-center gap-2 text-sm text-ink-2">
                  <span className="h-3 w-3 rounded-full bg-gold" />
                  Gastos
                </span>
              </div>
            </div>
            <div className="h-[320px] md:h-[380px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorIngresos" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#4A3A8A" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#4A3A8A" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorGastos" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#C9A227" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#C9A227" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EEEBFF" vertical={false} />
                  <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: "#9E96B8", fontSize: 14 }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: "#9E96B8", fontSize: 14 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="ingresos" stroke="#4A3A8A" strokeWidth={2.5} fill="url(#colorIngresos)" />
                  <Area type="monotone" dataKey="gastos" stroke="#C9A227" strokeWidth={2.5} fill="url(#colorGastos)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
